import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { http, formatDetail, openSignedDownload, auDateTime } from "../../../portal/api";

const STATUSES = ["Submitted", "Under review", "Clarification requested", "Accepted", "Not accepted"];
const input = "field-dark px-3 py-2 text-xs";

export default function EvidenceReviewTab({ engagementId }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(() => {
    http.get(`/portal/admin/engagements/${engagementId}/evidence`)
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Could not load evidence."));
  }, [engagementId]);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    try {
      await http.patch(`/portal/admin/evidence/${id}`, body);
      toast.success("Evidence updated.");
      load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  const draft = (id) => drafts[id] || {};
  const setDraft = (id, k, v) => setDrafts({ ...drafts, [id]: { ...draft(id), [k]: v } });

  if (items === null) return <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone2">Loading…</p>;
  if (items.length === 0) return <p className="text-sm text-stone2 border border-line bg-surface1 p-8" data-testid="admin-evidence-empty">No evidence submitted yet.</p>;

  return (
    <div className="space-y-px bg-line/40 border border-line/40" data-testid="admin-evidence-list">
      {items.map((it, i) => (
        <article key={it.id} data-testid={`admin-evidence-row-${i}`} className="bg-ink p-5 md:p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
            <h3 className="font-serif text-lg">{it.title}</h3>
            <div className="flex items-center gap-3">
              <select data-testid={`evidence-status-select-${i}`} className={input} value={it.status} onChange={(e) => patch(it.id, { status: e.target.value })}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <button onClick={() => openSignedDownload(`/portal/admin/evidence/${it.id}/download-link`).catch((err) => toast.error(formatDetail(err.response?.data?.detail)))} data-testid={`admin-evidence-download-${i}`} className="font-mono text-[11px] uppercase tracking-[0.12em] px-3 py-1.5 border border-line text-stone2 hover:border-ember hover:text-ember transition-colors duration-300 flex items-center gap-1.5">
                <Download size={11} /> File
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 font-mono text-[10px] text-stone2 mb-3">
            <span className="px-2 py-0.5 border border-line">{it.original_filename}</span>
            <span className="px-2 py-0.5 border border-line">{it.classification}</span>
            <span className="px-2 py-0.5 border border-line">PI: {it.personal_information_present}</span>
            <span className={`px-2 py-0.5 border ${it.cultural_knowledge?.contains !== "No" ? "border-ochre text-ochre" : "border-line"}`}>Cultural knowledge: {it.cultural_knowledge?.contains}</span>
            <span className="px-2 py-0.5 border border-line">By {it.uploaded_by} · {auDateTime(it.created_at)}</span>
            <span className="px-2 py-0.5 border border-line">Scan: {it.scan_status}</span>
          </div>
          <button onClick={() => setOpen(open === it.id ? null : it.id)} data-testid={`admin-evidence-expand-${i}`} className="font-mono text-[11px] uppercase tracking-[0.15em] text-ochre hover:text-ember transition-colors duration-300">
            {open === it.id ? "Hide review" : "Review details"}
          </button>
          {open === it.id && (
            <div className="mt-4 border-t border-line/60 pt-4 space-y-4 text-sm text-stone2">
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-1.5">
                {[["Owner", it.document_owner], ["Source", it.source], ["Date", it.document_date], ["Authority to provide", it.authority_to_provide], ["Cultural restrictions", it.cultural_restrictions || "—"], ["Permitted purpose", it.permitted_purpose], ["Retention", it.retention_requirements]].map(([l, v]) => (
                  <p key={l}><span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ochre">{l}: </span>{v}</p>
                ))}
              </div>
              {it.cultural_knowledge?.contains !== "No" && (
                <div className="border border-ochre/50 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre mb-2">Cultural authority record</p>
                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-1.5">
                    {[["Nation/community", it.cultural_knowledge?.nation_community], ["Cultural authority", it.cultural_knowledge?.cultural_authority], ["Permission status", it.cultural_knowledge?.permission_status], ["Restrictions", it.cultural_knowledge?.restrictions], ["Beneficiaries", it.cultural_knowledge?.intended_beneficiaries], ["May be quoted", it.cultural_knowledge?.may_be_quoted], ["May appear in final report", it.cultural_knowledge?.may_appear_in_final_report], ["Review before publication", it.cultural_knowledge?.required_review_before_publication]].map(([l, v]) => (
                      <p key={l}><span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ochre">{l}: </span>{v || "—"}</p>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone2 block mb-1">Clarification request (visible to client)</label>
                  <textarea data-testid={`clarification-input-${i}`} rows={2} className="field-dark w-full px-3 py-2 text-sm" defaultValue={it.clarification_note} onBlur={(e) => setDraft(it.id, "clarification_note", e.target.value)} />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone2 block mb-1">Internal notes (never visible to client)</label>
                  <textarea data-testid={`admin-notes-input-${i}`} rows={2} className="field-dark w-full px-3 py-2 text-sm" defaultValue={it.admin_notes} onBlur={(e) => setDraft(it.id, "admin_notes", e.target.value)} />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-5">
                <input data-testid={`reviewer-input-${i}`} className="field-dark px-3 py-2 text-sm" placeholder="Assigned reviewer" defaultValue={it.assigned_reviewer} onBlur={(e) => setDraft(it.id, "assigned_reviewer", e.target.value)} />
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" data-testid={`authority-confirmed-${i}`} checked={draft(it.id).authority_confirmed ?? it.authority_confirmed} onChange={(e) => setDraft(it.id, "authority_confirmed", e.target.checked)} className="accent-[#B44A2C]" />
                  Authority & restrictions confirmed
                </label>
                <button onClick={() => patch(it.id, draft(it.id))} data-testid={`save-review-${i}`} className="font-mono text-xs uppercase tracking-[0.15em] px-5 py-2 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300">Save review</button>
              </div>
            </div>
          )}
        </article>
      ))}
    </div>
  );
}
